<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import type { FmeaNode } from '@/types/domain';

const props = defineProps<{ node: FmeaNode | null; saving?: boolean }>();
const emit = defineEmits<{
  close: [];
  submit: [values: { severity: number | null; occurrence: number | null; detection: number | null }];
}>();

const severity = ref<number | null>(null);
const occurrence = ref<number | null>(null);
const detection = ref<number | null>(null);

watch(() => props.node, (node) => {
  severity.value = node?.severity ?? null;
  occurrence.value = node?.occurrence ?? null;
  detection.value = node?.detection ?? null;
}, { immediate: true });

/**
 * AIAG-VDA 评分口径，与后端 fmeaAiService 里发给模型的那份标尺一致。
 * 打分时把口径摆在眼前，比让人回去翻手册靠谱。
 */
const GUIDE = {
  severity: [
    ['10', '影响车辆/人员安全或违反法规，无预警'],
    ['9', '影响安全或法规，有预警'],
    ['7-8', '主要功能丧失或严重降级，客户强烈不满'],
    ['5-6', '次要功能丧失或降级，客户不满'],
    ['2-4', '外观/舒适性等轻微影响'],
    ['1', '无可辨识影响'],
  ],
  occurrence: [
    ['10', '无预防控制或几乎必然发生；新过程无经验'],
    ['8-9', '预防控制弱，历史上频繁发生'],
    ['5-7', '预防控制中等，偶有发生'],
    ['2-4', '预防控制较强，很少发生'],
    ['1', '通过消除机理或防错彻底预防'],
  ],
  detection: [
    ['10', '无探测控制或无法探测'],
    ['7-9', '探测手段间接/事后，把握低'],
    ['5-6', '过程检验/首件确认等，中等把握'],
    ['2-4', 'SPC/自动检测且样本充分，把握较高'],
    ['1', '设计上无法发生或必被探测（防错）'],
  ],
} as const;

/** 与后端 fmea-ap.ts 同一张表；改这里必须同步改后端，两边不一致比没有更糟 */
function band(value: number): number {
  if (value >= 8) return 0;
  if (value >= 6) return 1;
  if (value >= 4) return 2;
  if (value >= 2) return 3;
  return 4;
}
function severityBand(value: number): number {
  if (value >= 9) return 0;
  if (value >= 7) return 1;
  if (value >= 4) return 2;
  if (value >= 2) return 3;
  return 4;
}
const AP_TABLE: readonly string[][][] = [
  [['H','H','H','H','H'],['H','H','H','H','H'],['H','H','H','H','M'],['H','H','M','M','L'],['L','L','L','L','L']],
  [['H','H','H','H','H'],['H','H','H','M','M'],['H','H','M','M','L'],['M','M','M','L','L'],['L','L','L','L','L']],
  [['H','H','M','M','L'],['M','M','M','L','L'],['M','M','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L']],
  [['M','M','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L']],
  [['L','L','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L'],['L','L','L','L','L']],
];

/** 边调边看风险等级变化，这是 FMEA 评分该有的反馈 */
const livePriority = computed(() => {
  const s = severity.value; const o = occurrence.value; const d = detection.value;
  if (s === null || o === null || d === null) return null;
  return AP_TABLE[severityBand(s)][band(o)][band(d)];
});
const liveRpn = computed(() => {
  const s = severity.value; const o = occurrence.value; const d = detection.value;
  return s === null || o === null || d === null ? null : s * o * d;
});
const AP_LABEL: Record<string, string> = { H: '高', M: '中', L: '低' };

const scores = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function submit() {
  emit('submit', {
    severity: severity.value,
    occurrence: occurrence.value,
    detection: detection.value,
  });
}
function clearAll() {
  severity.value = null;
  occurrence.value = null;
  detection.value = null;
}
function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.node && !props.saving) emit('close');
}
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="node" class="rating-mask" @click.self="!saving && emit('close')">
    <section class="rating-dialog" role="dialog" aria-modal="true" aria-labelledby="rating-title">
      <header>
        <div>
          <span>AIAG-VDA / S · O · D</span>
          <h3 id="rating-title">失效模式评分</h3>
          <p>{{ node.title }}</p>
        </div>
        <button class="inline-button" type="button" :disabled="saving" @click="emit('close')">关闭</button>
      </header>

      <div class="rating-body">
        <div
          v-for="dim in ([
            { key: 'severity', label: '严重度 S', model: severity, guide: GUIDE.severity },
            { key: 'occurrence', label: '频度 O', model: occurrence, guide: GUIDE.occurrence },
            { key: 'detection', label: '探测度 D', model: detection, guide: GUIDE.detection },
          ] as const)"
          :key="dim.key"
          class="rating-dim"
        >
          <b>{{ dim.label }}</b>
          <div class="score-row" role="radiogroup" :aria-label="dim.label">
            <button
              v-for="n in scores"
              :key="n"
              type="button"
              role="radio"
              :aria-checked="dim.model === n"
              :class="{ picked: dim.model === n }"
              @click="dim.key === 'severity' ? (severity = severity === n ? null : n)
                    : dim.key === 'occurrence' ? (occurrence = occurrence === n ? null : n)
                    : (detection = detection === n ? null : n)"
            >{{ n }}</button>
          </div>
          <dl class="rating-guide">
            <template v-for="row in dim.guide" :key="row[0]">
              <dt>{{ row[0] }}</dt><dd>{{ row[1] }}</dd>
            </template>
          </dl>
        </div>
      </div>

      <footer>
        <div class="rating-result">
          <template v-if="livePriority">
            <b class="rating-ap" :class="`ap-${livePriority.toLowerCase()}`">
              AP {{ livePriority }} · {{ AP_LABEL[livePriority] }}
            </b>
            <span>RPN {{ liveRpn }}（仅参考，德系客户不接受用它替代 AP）</span>
          </template>
          <span v-else class="rating-incomplete">三项都选齐才会给出 AP —— 缺项按「未评价」处理，不会当成低风险</span>
        </div>
        <div class="rating-actions">
          <button class="button" type="button" :disabled="saving" @click="clearAll">清空评分</button>
          <button class="button button-primary" type="button" :disabled="saving" @click="submit">
            {{ saving ? '保存中…' : '保存评分' }}
          </button>
        </div>
      </footer>
    </section>
  </div>
</template>

<style scoped>
/* 右侧抽屉：1561 条失效逐条评分，左边的结构树必须一直看得见，
   否则每评一条都要关掉弹窗重新找位置。 */
.rating-mask { position: fixed; inset: 0; z-index: 60; display: flex; justify-content: flex-end; background: rgba(15,23,36,.4); }
.rating-dialog { width: min(680px, 92vw); height: 100%; overflow-y: auto; background: var(--white); border-left: 1px solid var(--line); box-shadow: -10px 0 40px rgba(15,23,36,.18); animation: rating-in 180ms ease-out; }
@keyframes rating-in { from { transform: translateX(24px); opacity: .6; } to { transform: none; opacity: 1; } }
.rating-dialog > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.rating-dialog header span { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.rating-dialog header h3 { margin: 3px 0 0; font-size: 18px; }
.rating-dialog header p { margin: 4px 0 0; max-width: 60ch; color: var(--ink); font-size: 13px; line-height: 1.6; }
.rating-body { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 18px; padding: 16px 20px; }
.rating-dim > b { display: block; margin-bottom: 6px; font-size: 14px; }
.score-row { display: flex; flex-wrap: wrap; gap: 4px; }
.score-row button { width: 34px; height: 34px; border: 1px solid var(--line); background: var(--white); color: var(--ink); font-size: 13px; font-weight: 700; cursor: pointer; }
.score-row button:hover { border-color: var(--blue); }
.score-row button.picked { border-color: var(--blue); background: var(--blue); color: white; }
.rating-guide { display: grid; grid-template-columns: 46px 1fr; gap: 2px 8px; margin: 10px 0 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.rating-guide dt { font-weight: 700; }
.rating-guide dd { margin: 0; }
.rating-dialog footer { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 14px 20px; border-top: 1px solid var(--line); background: var(--paper); }
.rating-result { display: grid; gap: 3px; }
.rating-ap { padding: 2px 8px; border-radius: 3px; font-size: 15px; font-weight: 800; justify-self: start; }
.rating-ap.ap-h { background: #fdecea; color: var(--risk); }
.rating-ap.ap-m { background: #fdf3e3; color: var(--warning); }
.rating-ap.ap-l { background: #eef7ec; color: var(--green); }
.rating-result span { color: var(--muted); font-size: 12px; }
.rating-incomplete { max-width: 52ch; }
.rating-actions { display: flex; gap: 8px; }

@media (max-width: 600px) {
  .rating-dialog { width: 100%; }
}
</style>
